import { useState } from 'react';
import { Loader2, Copy, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useDraftIntroduction, useSaveIntroduction } from '@/hooks/useIntroductions';

interface IntroductionDialogProps {
  matchId: string;
  open: boolean;
  onClose: () => void;
}

export function IntroductionDialog({ matchId, open, onClose }: IntroductionDialogProps) {
  const [tone, setTone] = useState('warm');
  const [length, setLength] = useState('medium');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [hasGenerated, setHasGenerated] = useState(false);

  const draftMutation = useDraftIntroduction();
  const saveMutation = useSaveIntroduction();
  const { toast } = useToast();

  const handleGenerate = async () => {
    try {
      const result = await draftMutation.mutateAsync({
        match_id: matchId,
        tone,
        length,
      });
      setSubject(result.subject);
      setBody(result.body);
      setHasGenerated(true);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleCopy = async () => {
    const text = `Subject: ${subject}\n\n${body}`;
    await navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard' });
  };

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync({
        matchId,
        subject,
        body,
        sentVia: 'clipboard',
      });
      toast({ title: 'Introduction saved', description: 'Match marked as introduced' });
      onClose();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Draft Introduction</DialogTitle>
        </DialogHeader>

        {!hasGenerated ? (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Length</Label>
                <Select value={length} onValueChange={setLength}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Short (~75 words)</SelectItem>
                    <SelectItem value="medium">Medium (~150 words)</SelectItem>
                    <SelectItem value="long">Long (~250 words)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={handleGenerate} disabled={draftMutation.isPending} className="w-full">
              {draftMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Generate Draft
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Body</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                className="font-mono text-sm"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {hasGenerated && (
            <>
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={draftMutation.isPending}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate
              </Button>
              <Button variant="outline" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                Copy to Clipboard
              </Button>
              <Button onClick={handleSave} disabled={saveMutation.isPending}>
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save & Mark Introduced
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
